import { notification } from 'antd';
import { match } from 'ts-pattern';

import { ApolloClient, from, HttpLink, InMemoryCache, NormalizedCacheObject } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { setContext } from '@apollo/client/link/context';
import { relayStylePagination } from '@apollo/client/utilities';
import type { XProps } from '../types/global';

/**
 * Keep the feature switch header small enough for edge proxies.
 *
 * The parent Crimson app passes a much larger feature switch object into Roadmap.
 * Only include flags that backend GraphQL services may use for business logic.
 * Frontend-only UI flags should stay out of this request header.
 */
const FEATURE_SWITCH_HEADER_MAX_BYTES = 1024;
const FEATURE_SWITCH_HEADER_WHITELIST = [
  'DASHBOARD_SERVICE_UPDATES',
  'GUARDIAN_ROLE',
  'MULTI_SSM',
  'REFLECTIONS',
  'NEW_COVER',
  'NEW_BADGES_SC',
  'GROUP_MISSION',
  'ACADEMIC_ADVISOR_ROLE',
  'AUTO_CREATE_ESSAY_FOLDER',
  'SSA_SC_INTEGRATION',
  'COPILOT_ROADMAP',
  'ROADMAP_REPORT_GPT_5_2',
  'STUDENT_ONBOARDING_ROADMAP_INTEGRATION',
  'MISSION_LIBRARY',
  'INDIGO_SOP',
  'STRATEGIST_KPI',
  'AI_MISSION_UPDATES',
  'SESSION_SUMMARY_TO_MISSIONS',
  'SESSION_SUMMARY_TO_MISSIONS_V1',
  'SESSION_SUMMARY_TO_MISSIONS_V2',
];

const getFeatureSwitchHeaderValue = (featureSwitches?: Record<string, boolean>) => {
  const filteredFlags: Record<string, boolean> = {};

  for (const flagKey of FEATURE_SWITCH_HEADER_WHITELIST) {
    if (!featureSwitches || !(flagKey in featureSwitches)) {
      continue;
    }

    const nextFlags = {
      ...filteredFlags,
      [flagKey]: featureSwitches[flagKey],
    };
    const nextHeader = JSON.stringify(nextFlags);

    if (nextHeader.length > FEATURE_SWITCH_HEADER_MAX_BYTES) {
      break;
    }

    filteredFlags[flagKey] = featureSwitches[flagKey];
  }

  return JSON.stringify(filteredFlags);
};

// Log any GraphQL errors or network error that occurred
const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.map(({ extensions }) => {
      return notification.error({
        message: match(extensions?.code)
          .with('UNAUTHORIZED', () => 'We are Sorry...')
          .otherwise(() => 'Error'),
        description: match(extensions?.code)
          .with(
            'UNAUTHORIZED',
            () => "The page you're trying to access has restricted access. Please refer to your system administrator. ",
          )
          .otherwise(() => 'Oops! Something went wrong at our end. Please try again later.'),
        icon: <div>&#128533;</div>,
      });
    });
  }

  if (networkError) {
    notification.error({
      message: match(networkError)
        .when(
          (e) => 'statusCode' in e && e.statusCode === 401,
          () => 'Error',
        )
        .when(
          (e) => 'statusCode' in e && e.statusCode === 403,
          () => 'We are Sorry...',
        )
        .otherwise(() => 'Problem Connecting'),
      description: match(networkError)
        .when(
          (e) => 'statusCode' in e && e.statusCode === 401,
          () => 'This account is not authenticated. Please refer to your system administrator. ',
        )
        .when(
          (e) => 'statusCode' in e && e.statusCode === 403,
          () => "The page you're trying to access has restricted access. Please refer to your system administrator. ",
        )
        .otherwise(() => 'Uh-oh! The network connection is lost. Please check your connectivity and try again.'),
      icon: <div>&#128533;</div>,
    });
    if ('statusCode' in networkError && networkError.statusCode === 403) {
      window.xprops?.authorize();
    }
  }
});

const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: Infinity,
    jitter: true,
  },
  attempts: {
    max: 3,
    retryIf: async (error) => {
      if ('statusCode' in error && error.statusCode === 403) {
        const result = await window.xprops?.authorize();
        return !result;
      }
      return false;
    },
  },
});

const getHttpLink = (endpoint: string, xprops?: XProps) => {
  const { featureSwitches, user, domain } = xprops ?? {};
  const { roleInfo, tenant } = user || {};
  const defaultHeaders: { [key: string]: string } = {
    'X-Feature-Switches': getFeatureSwitchHeaderValue(featureSwitches),
  };
  if (/(core-progress)|(core-student-center)|(localhost)|(app.crimson)/.test(endpoint)) {
    console.log({ user, roleInfo });
    defaultHeaders['x-user-id'] = user?.userId || '';
    defaultHeaders['x-user-role'] = roleInfo ? Buffer.from(roleInfo).toString('base64') : '';
    defaultHeaders['x-tenant-domain'] = domain ? new URL(domain).hostname : '';
    defaultHeaders['x-user-tenant'] = tenant ? `${tenant.name}:${tenant.level}` : '';
  }
  if (/localhost/.test(endpoint)) {
    defaultHeaders['x-user'] = JSON.stringify({
      userId: xprops?.userId ? xprops.userId : 'auth0-strategist',
      roles: xprops?.user?.role ? [{ id: xprops.user.role }] : [{ id: 'STRATEGIST' }, { id: 'STUDENT' }],
      tenant: tenant ? tenant.name : 'crimsonapp',
      level: tenant ? tenant.level : 3,
    });
  }
  return new HttpLink({
    uri: endpoint,
    headers: defaultHeaders,
  });
};

const authMiddleware = setContext(async () => {
  // get token from parent frame app.
  let token = await window.xprops?.getBearer();
  if (!token || token.length < 32) {
    await window.xprops?.authorize();
    token = await window.xprops?.getBearer();
  }
  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
});

export function getGraphQLClient(endpoint: string, xprops?: XProps): ApolloClient<NormalizedCacheObject> {
  return new ApolloClient({
    link: from([errorLink, authMiddleware, retryLink, getHttpLink(endpoint, xprops)]),
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            resourceConnection: relayStylePagination(),
            tagConnection: relayStylePagination(),
          },
        },
      },
    }),
    defaultOptions: {
      query: {
        fetchPolicy: 'cache-first',
        errorPolicy: 'all',
      },
      mutate: {
        errorPolicy: 'all',
      },
    },
  });
}
